import React from 'react'
import { observable, reaction, makeObservable } from 'mobx'
import i18n from 'i18next'
import * as R from 'ramda'
import { v4 as uuidv4 } from 'uuid'
import NewWidget from './models/NewWidget'
import StagedForm from '../StagedForm'
import { getIndustries } from '../../../../utils/helpers'
import { getQuestionTitle, insertInheritedOptions } from '../../../../utils/QuestionHelper'
import { stripOutHtml, unescapeHtmlTags, hasRequiredDependencies } from '../../../helpers'
import SB_SVG from '../../../../assets/images/stacked_bar.svg'
import SC_SVG from '../../../../assets/images/stacked_column.svg'
import Typography from '../../../../UniformComponents/Typography'
import Slider from '../../../../UniformComponents/Slider'

class WidgetCreatorStore {
    constructor(dashStore) {
        makeObservable(this)
        this.appStore = dashStore.appStore
        this.dashStore = dashStore
        this.newWidget = new NewWidget(undefined, undefined, dashStore.appStore)

        reaction(() => [dashStore.scopeName], () => this.setDataLevels())
    }

    @observable loading = false

    @observable loadingQuestions = false

    @observable error = false

    @observable currentStage = 0

    @observable show = false

    @observable questions = []

    @observable detailedQuestions = []

    @observable newWidget = new NewWidget()

    @observable portfolios = []

    @observable divisions = []

    @observable widgetType = 'single'

    @observable showWidgetType = false

    @observable showWidgetCreator = false

    @observable multiQuestions = new Map()

    @observable multiDetailedQuestions = new Map()

    @observable fullSurvey = {}

    @observable registrationDataQuestions = []

    hiddenQuestionIds = new Set()

    mappedQuestionIds = new Set()

    @observable showWidgetCreatorV3 = false

    @observable isEditSetupInProgress = false

    @observable openDataLevelAccordian = false

    @observable showAdvReportingSettings = false

    advReportingSettings = { label: i18n.t('advanced'), name: 'showAdvReportingSettings' }

    detailedQuestionsMap = new Map()

    benchmarkTags = []

    kpis = []

    displayAsOptions = {
        average: { label: 'Average', value: 'avg' },
        averageNF: { label: 'Average', value: 'NFOption' },
        pct: { label: 'Response Percentage', value: 'pct' },
        byRow: { label: 'By Option', value: 'pct' },
        nps: { label: 'Net Promoter Score', value: 'nps' },
        total: { label: 'Total', value: 'total' },
    }

    productMap = {}

    questionDisplayAs = {
        RD: {
            numeric: ['average', 'pct'].map((d) => this.displayAsOptions[d]),
            nonNumeric: ['pct'].map((d) => this.displayAsOptions[d]),
            nps: ['pct', 'nps'].map((d) => this.displayAsOptions[d]),
        },
        CH: {
            numeric: ['pct'].map((d) => this.displayAsOptions[d]),
            nonNumeric: ['pct'].map((d) => this.displayAsOptions[d]),
        },
        TB: {
            numeric: ['average', 'pct'].map((d) => this.displayAsOptions[d]),
            nonNumeric: ['pct'].map((d) => this.displayAsOptions[d]),
        },
        'TB - row': {
            numeric: ['average', 'pct'].map((d) => this.displayAsOptions[d]),
            nonNumeric: ['pct'].map((d) => this.displayAsOptions[d]),
        },
        SB: {
            numeric: ['average', 'pct'].map((d) => this.displayAsOptions[d]),
            nonNumeric: ['pct'].map((d) => this.displayAsOptions[d]),
        },
        OD: {
            numeric: ['pct'].map((d) => this.displayAsOptions[d]),
        },
        'OD - row': {
            numeric: ['pct'].map((d) => this.displayAsOptions[d]),
        },
        SG: {
            numeric: ['average'].map((d) => this.displayAsOptions[d]),
            numericEvent: ['average', 'pct'].map((d) => this.displayAsOptions[d]),
        },
        NF: {
            numeric: ['average', 'byRow'].map((d) => this.displayAsOptions[d]),
        },
        'NF - row': {
            numeric: ['average'].map((d) => this.displayAsOptions[d]),
        },
        I: {
            numeric: ['average'].map((d) => this.displayAsOptions[d]),
        },
        PC: {
            numeric: ['pct'].map((d) => this.displayAsOptions[d]),
        },
        TX: {
            nonNumeric: ['pct'].map((d) => this.displayAsOptions[d]),
        },
        TA: {
            nonNumeric: ['pct'].map((d) => this.displayAsOptions[d]),
        },
    }

    blockedQuestions = ['TX', 'TA', 'TL', 'RT', 'CO', 'UV']

    blockedEventLevelQuestions = ['TL', 'RT', 'CO', 'UV']

    dataLevelOptions = {
        3: { value: 'client', label: 'Company' },
        4: { value: 'division', label: 'Division' },
        5: { value: 'portfolio', label: 'Portfolio' },
        6: { value: 'product', label: 'Event' },
    }

    dataLevels = []

    timePeriodOptions = {
        client: [
            { value: 'YOY', label: 'Year On Year' },
            { value: 'ROL-36', label: 'Last 36 Months' },
            { value: 'ROL-84', label: 'Long-term' },
        ],
        division: [
            { value: 'YOY', label: 'Year On Year' },
            { value: 'ROL-36', label: 'Last 36 Months' },
            { value: 'ROL-84', label: 'Long-term' },
        ],
        portfolio: [
            { value: 'YOY', label: 'Year On Year' },
            { value: 'ROL-36', label: 'Last 36 Months' },
            { value: 'ROL-84', label: 'Long-term' },
        ],
        product: [
            { value: 'YOY', label: 'Year On Year' },
            { value: 'last-3', label: 'Last 3 Months' },
            { value: 'last-6', label: 'Last 6 Months' },
            { value: 'last-12', label: 'Last 12 Months' },
            { value: 'last-24', label: 'Last 24 Months' },
        ],
    }

    questionChartOptions = {
        quadrantAnalysis: {
            currentLevel: ['quadrantAnalysis'],
            levelBelow: ['quadrantAnalysis'],
        },
        KPI: {
            currentLevel: [
                'gauge', 'column', 'bar', 'stackedColumn', 'stackedBar', 'donut', 'pie', 'treemap', 'text',
            ],
            levelBelow: ['column', 'bar', 'stackedColumn', 'stackedBar'],
        },
        multiQuestion: {
            currentLevel: ['column', 'bar', 'stackedColumn', 'stackedBar', 'multiText'],
            levelBelow: ['column', 'bar', 'stackedColumn', 'stackedBar', 'line'],
        },
        LMX: {
            currentLevel: ['donut', 'pie', 'column', 'bar', 'stackedColumn', 'stackedBar', 'line', 'treemap', 'lmx'],
            levelBelow: ['column', 'bar', 'stackedColumn', 'stackedBar', 'line'],
        },
        heat: {
            currentLevel: ['heat'],
            levelBelow: [],
        },
        VMAT: {
            currentLevel: ['VMAT'],
            levelBelow: [],
        },
        RD: {
            numeric: {
                currentLevel: {
                    pct: ['donut', 'pie', 'column', 'bar', 'stackedColumn', 'stackedBar', 'line', 'treemap'],
                    avg: ['gauge', 'column', 'bar', 'text'],
                },
                levelBelow: {
                    pct: ['column', 'bar', 'stackedColumn', 'stackedBar', 'line'],
                    avg: ['column', 'bar', 'line'],
                },
            },
            nonNumeric: {
                currentLevel: {
                    pct: ['donut', 'pie', 'column', 'bar', 'stackedColumn', 'stackedBar', 'line', 'treemap'],
                },
                levelBelow: {
                    pct: ['column', 'bar', 'stackedColumn', 'stackedBar', 'line'],
                },
            },
            nps: {
                currentLevel: ['gauge', 'column', 'bar', 'text'],
                levelBelow: ['column', 'bar', 'stackedColumn', 'stackedBar', 'line'],
            },
        },
        CH: {
            numeric: {
                currentLevel: {
                    pct: ['column', 'bar', 'stackedColumn', 'stackedBar', 'line'],
                },
                levelBelow: {
                    pct: ['column', 'bar', 'stackedColumn', 'stackedBar', 'line'],
                },
            },
            nonNumeric: {
                currentLevel: {
                    pct: ['column', 'bar', 'stackedColumn', 'stackedBar', 'line'],
                },
                levelBelow: {
                    pct: ['column', 'bar', 'stackedColumn', 'stackedBar', 'line'],
                },
            },
        },
        TB: {
            numeric: {
                currentLevel: {
                    pct: ['column', 'bar', 'stackedColumn', 'stackedBar', 'line'],
                    avg: ['column', 'bar', 'line'],
                },
                levelBelow: {
                    pct: ['column', 'bar', 'stackedColumn', 'stackedBar', 'line'],
                    avg: ['column', 'bar', 'line'],
                },
            },
            nonNumeric: {
                currentLevel: {
                    pct: ['column', 'bar', 'stackedColumn', 'stackedBar', 'line'],
                },
                levelBelow: {
                    pct: ['column', 'bar', 'stackedColumn', 'stackedBar', 'line'],
                },
            },
        },
        'TB - row': {
            numeric: {
                currentLevel: {
                    pct: ['donut', 'pie', 'column', 'bar', 'stackedBar', 'stackedColumn', 'line', 'treemap'],
                    avg: ['gauge', 'column', 'bar', 'text'],
                },
                levelBelow: {
                    pct: ['column', 'bar', 'stackedBar', 'stackedColumn', 'line'],
                    avg: ['column', 'bar', 'line'],
                },
            },
            nonNumeric: {
                currentLevel: {
                    pct: ['donut', 'pie', 'column', 'bar', 'stackedBar', 'stackedColumn', 'line', 'treemap'],
                },
                levelBelow: {
                    pct: ['column', 'bar', 'stackedBar', 'stackedColumn', 'line'],
                },
            },
        },
        SB: {
            numeric: {
                currentLevel: {
                    pct: ['donut', 'pie', 'column', 'bar', 'stackedBar', 'stackedColumn', 'line', 'treemap'],
                    avg: ['gauge', 'column', 'bar', 'text'],
                },
                levelBelow: {
                    pct: ['column', 'bar', 'stackedBar', 'stackedColumn', 'line'],
                    avg: ['column', 'bar', 'line'],
                },
            },
            nonNumeric: {
                currentLevel: {
                    pct: ['donut', 'pie', 'column', 'bar', 'stackedBar', 'stackedColumn', 'line', 'treemap'],
                },
                levelBelow: {
                    pct: ['column', 'bar', 'stackedBar', 'stackedColumn', 'line'],
                },
            },
        },
        OD: {
            numeric: {
                currentLevel: ['donut', 'pie', 'column', 'bar', 'stackedBar', 'stackedColumn', 'line', 'treemap'],
                levelBelow: ['column', 'bar', 'stackedBar', 'stackedColumn', 'line'],
            },
        },
        'OD - row': {
            numeric: {
                currentLevel: ['gauge', 'column', 'bar', 'line', 'text'],
                levelBelow: ['column', 'bar', 'line'],
            },
        },
        SG: {
            numeric: {
                currentLevel: ['gauge', 'column', 'bar', 'line', 'text'],
                levelBelow: ['column', 'bar', 'line'],
            },
        },
        NF: {
            numeric: {
                currentLevel: {
                    avg: ['column', 'bar', 'line', 'text'],
                    pct: ['donut', 'pie', 'column', 'bar', 'stackedBar', 'stackedColumn', 'line', 'treemap'],
                },
                levelBelow: {
                    avg: ['column', 'bar', 'line'],
                    pct: ['column', 'bar', 'stackedBar', 'stackedColumn', 'line'],
                },
            },
        },
        'NF - row': {
            numeric: {
                currentLevel: {
                    avg: ['column', 'bar', 'line', 'text', 'treemap'],
                },
                levelBelow: {
                    avg: ['column', 'bar', 'line'],
                },
            },
        },
        I: {
            numeric: {
                currentLevel: ['column', 'bar', 'line', 'text'],
                levelBelow: ['column', 'bar', 'line'],
            },
        },
        PC: {
            numeric: {
                currentLevel: ['donut', 'pie', 'column', 'bar', 'stackedBar', 'stackedColumn', 'line'],
                levelBelow: ['column', 'bar', 'stackedBar', 'stackedColumn', 'line'],
            },
        },
        TX: {
            nonNumeric: {
                currentLevel: ['textResponse', 'wordCloud'],
                levelBelow: ['textResponse', 'wordCloud'],
            },
        },
        TA: {
            nonNumeric: {
                currentLevel: ['textResponse', 'wordCloud'],
                levelBelow: ['textResponse', 'wordCloud'],
            },
        },

    }

    chartTypes = {
        gauge: {
            label: 'Gauge',
            value: 'gauge',
            src: 'fa-regular fa-gauge',
            isIcon: true,
            iconLeft: true,
        },
        // 'gauge0-5': {
        //     label: 'Gauge 0-5',
        //     value: 'gauge0-5',
        //     src: 'fa-regular fa-gauge',
        //     isIcon: true,
        //     iconLeft: true,
        // },
        column: {
            label: 'Column',
            value: 'column',
            src: 'fa-sharp fa-light fa-chart-column',
            isIcon: true,
            iconLeft: true,
        },
        bar: {
            label: 'Bar',
            value: 'bar',
            src: 'fa-sharp fa-regular fa-chart-bar',
            isIcon: true,
            iconLeft: true,
        },
        stackedColumn: {
            label: 'Stacked Column',
            value: 'stackedColumn',
            src: SC_SVG,
            isIcon: false,
            iconLeft: true,
        },
        stackedBar: {
            label: 'Stacked Bar',
            value: 'stackedBar',
            src: SB_SVG,
            isIcon: false,
            iconLeft: true,
        },
        donut: {
            label: 'Donut',
            value: 'donut',
            src: 'fa-light fa-donut',
            isIcon: true,
            iconLeft: true,
        },
        pie: {
            label: 'Pie',
            value: 'pie',
            src: 'fa-solid fa-chart-pie',
            isIcon: true,
            iconLeft: true,
        },
        treemap: {
            label: 'Treemap',
            value: 'treemap',
            src: 'fa-solid fa-chart-tree-map',
            isIcon: true,
            iconLeft: true,
        },
        text: {
            label: 'Text',
            value: 'text',
            src: 'fa-sharp-duotone fa-thin fa-align-center',
            isIcon: true,
            iconLeft: true,
        },
        textResponse: {
            label: 'Text Response',
            value: 'textResponse',
            src: 'fa-light fa-align-left',
            isIcon: true,
            iconLeft: true,
        },
        wordCloud: {
            label: 'Word Cloud',
            value: 'wordCloud',
            src: 'fa-regular fa-cloud-word',
            isIcon: true,
            iconLeft: true,
        },
        line: {
            label: 'Line',
            value: 'line',
            src: 'fa-solid fa-chart-line',
            isIcon: true,
            iconLeft: true,
        },
        // radar: {
        //     label: 'Radar',
        //     value: 'radar',
        //     src: 'fa-sharp-duotone fa-regular fa-chart-radar',
        //     isIcon: true,
        //     iconLeft: true,
        // },
        multiText: {
            label: 'Multi-Text',
            value: 'multiText',
            src: 'fa-sharp fa-light fa-line-columns',
            isIcon: true,
            iconLeft: true,
        },
        lmx: {
            label: 'Matrix',
            value: 'lmx',
            src: 'fa-light fa-chart-scatter-3d',
            isIcon: true,
            iconLeft: true,
        },
        heat: {
            label: 'Heat map',
            value: 'heat',
            src: 'fa-light fa-chart-scatter-3d',
            isIcon: true,
            iconLeft: true,
        },
        VMAT: {
            label: 'Verbatim Matrix',
            value: 'VMAT',
            src: 'fa-light fa-chart-scatter-3d',
            isIcon: true,
            iconLeft: true,
        },
        quadrantAnalysis: {
            label: 'Bubble chart',
            value: 'quadrantAnalysis',
            src: 'fa-regular fa-chart-scatter',
            isIcon: true,
            iconLeft: true,
        },
    }

    filterTree = []

    surveyTypes = []

    defaultLabelPositions = {
        pie: 'outside',
        donut: 'outside',
        bar: 'right',
        stackedColumn: 'inside',
        stackedBar: 'inside',
        default: 'top',
    }

    getDivisions = async () => {
        const { appStore } = this
        let divisions = []

        try {
            divisions = await appStore.http.get('/division')
        } catch (e) {
            console.error(e)
        }

        return divisions
    }

    init = async () => {
        const { appStore } = this
        this.loading = true
        const { prodMap, numOfMandatoryQuestions } = await this.getProdMap(true)
        this.productMap = prodMap
        this.questionCount = numOfMandatoryQuestions
        await this.getBenchmarkTags()
        await this.getNonQuestionData()
        const isMultiDivision = R.path(['userData', 'flags', 'isMultiDivision'], appStore)
        const promises = [appStore.http.get('/portfolio'), appStore.http.get('/product')]
        if (isMultiDivision) promises.push(this.getDivisions())
        const data = await Promise.all(promises)
        this.portfolios = data[0]
        this.products = data[1]
        if (isMultiDivision) this.divisions = data[2]
        this.setDataLevels()
        this.loading = false
    }

    setDataLevels = () => {
        const { oId } = this.dashStore
        const isMultiDivision = R.path(['userData', 'flags', 'isMultiDivision'], this.appStore)
        const dataLevelOptions = Object.keys(this.dataLevelOptions).reduce((dl, l) => {
            const num = parseInt(l, 10)
            if (num === 4 && !isMultiDivision) return dl
            if (num === 5 && this.portfolios.length < 2) return dl
            if (num < oId) return dl
            return [...dl, this.dataLevelOptions[l]]
        }, [])

        if (oId === 6 && this.fullSurvey?.survey?.metadata?.isParent) dataLevelOptions.push({ value: 'branch', label: 'Branch' })
        this.dataLevels = dataLevelOptions
    }

    createTreeData = (level, filters) => {
        const { dashStore } = this
        this.filterTree = dashStore.createTreeData(level, filters)
    }

    getQuestions = async (metric, forQuadrant = false) => {
        const { appStore } = this
        const { langcode } = appStore
        const { oId, recordId } = this.dashStore
        const { clientId } = appStore.userData
        const industry = R.pathOr(metric.industry, ['industry', 'value'], metric)
        const productType = R.pathOr(metric.productType, ['productType', 'value'], metric)
        const surveyType = R.pathOr(metric.surveyType, ['surveyType', 'value'], metric)
        const respondentType = R.pathOr(metric.respondentType, ['respondentType', 'value'], metric)
        if (!this.shouldGetQuestions(industry, productType, surveyType, respondentType)) return
        this.loadingQuestions = true
        if (oId === 6) {
            await this.getEventLevelQuestions(metric)
            this.loadingQuestions = false
            return
        }
        if (!productType || !surveyType || !industry || !respondentType) {
            this.loadingQuestions = false
            return
        }

        const promiseParamsArray = [
            [3, [[clientId, 3]]],
            [4, [[recordId, 4]]],
            [5, [[recordId, 5]]],
        ]

        const questions = await Promise.all(
            promiseParamsArray.map((p) => {
                if (p[0] === oId) {
                    if (forQuadrant) {
                        const respondentTypes = Object.values(R.pathOr(
                            {},
                            [
                                'productMap',
                                industry,
                                'children',
                                productType,
                                'children',
                                surveyType,
                                'children',
                            ],
                            this,
                        ))
                        return p[1].map((p1) => (
                            respondentTypes.map((rt) => this.getQlistQuestions(metric, p1[0], p1[1], rt.value))
                        ))
                    }
                    return p[1].map((p1) => this.getQlistQuestions(metric, p1[0], p1[1]))
                }
                return undefined
            }).flat(2),
        )

        this.questions = questions.flat(2).filter((q) => (
            !!q && !this.blockedQuestions.includes(q.question.questionTypeCode)
        ))
        this.detailedQuestions = this.formatQuestions(this.questions, langcode)
        const key = `${industry}_${productType}_${surveyType}_${respondentType}_false`
        this.detailedQuestionsMap.set(key, this.detailedQuestions)
        this.loadingQuestions = false
    }

    getQuestionLabel = (question, langcode, optionContent, includeQuestion = true, appendHiddenOrMapped = false) => {
        const initialLocale = R.pathOr('en_GB', ['metadata', 'initialLocale'], question)
        const questionContent = R.pathOr({}, ['content', 'shortTitle'], question)
        let optionLabel = ''
        if (optionContent) {
            optionLabel = R.pathOr('', ['text'], getQuestionTitle(
                {
                    content: optionContent,
                    locale: langcode,
                    initialLocale,
                },
            ))
        }

        if (!includeQuestion) return optionLabel

        let suffix = ''
        if (appendHiddenOrMapped) {
            // {HIDDEN} takes precedence
            if (this.mappedQuestionIds.has(question.id)) suffix += ' {MAPPED}'
            else if (this.hiddenQuestionIds.has(question.id)) suffix += ' {HIDDEN}'
        }

        const baseLabel = R.pathOr('', ['text'], getQuestionTitle(
            {
                content: questionContent,
                locale: langcode,
                initialLocale,
            },
        ))

        const finalLabel = `${baseLabel}${optionContent ? ` - ${optionLabel}` : ''}${suffix}`
        return finalLabel
    }

    formatQuestions = (questions, langcode, group, respondentType, appendHiddenOrMapped = false) => (
        questions
            .filter((q) => !!q)
            .map(({ question, parentId, priority }) => {
                const questionLabel = this.getQuestionLabel(question, langcode, undefined, true, appendHiddenOrMapped)
                if (!['FM', 'NF', 'OD', 'TB'].includes(question.questionTypeCode)) {
                    return ({
                        ...question,
                        group,
                        respondentType,
                        label: questionLabel,
                        value: question.id,
                        qlistParentId: parentId,
                        priority,
                    })
                }

                let questionPath = ['optionSet']
                if (question.questionTypeCode === 'OD') questionPath = ['optionSet', 'options']
                if (question.questionTypeCode === 'TB') questionPath = ['optionSetY', 'options']

                if (parentId) {
                    const parent = questions.find((q) => q.questionId === parentId)
                    insertInheritedOptions(question, parent)
                }

                const formQuestion = R.pathOr(
                    [],
                    questionPath,
                    question,
                ).map((o) => {
                    let questionOption = {
                        ...o.question,
                        questionTypeCode: question.questionTypeCode === 'FM' ? o.question.questionTypeCode : o.questionTypeCode || `${question.questionTypeCode} - row`,
                        label: unescapeHtmlTags(stripOutHtml(this.getQuestionLabel(
                            question,
                            langcode,
                            ['TB', 'OD'].includes(question.questionTypeCode)
                                ? R.pathOr({}, ['langs'], o)
                                : R.pathOr({}, ['question', 'content', 'shortTitle'], o),
                            true,
                            appendHiddenOrMapped,
                        ))),
                        value: R.pathOr('', ['TB', 'OD'].includes(question.questionTypeCode) ? ['id'] : ['question', 'id'], o),
                        parentId: question.questionTypeCode !== 'FM' ? question.id : undefined,
                        formId: question.questionTypeCode === 'FM' ? question.id : undefined,
                        group,
                        respondentType,
                        qlistParentId: parentId,
                        priority,
                    }
                    if (['TB', 'OD'].includes(question.questionTypeCode)) {
                        questionOption = { ...question, ...questionOption }
                    }
                    return questionOption
                })
                if (question.questionTypeCode !== 'FM') {
                    formQuestion.push({
                        ...question,
                        group,
                        respondentType,
                        label: `${questionLabel} - Total`,
                        value: question.id,
                        qlistParentId: parentId,
                        priority,
                    })
                }
                return formQuestion
            }).flat(2)
    )

    setRegistrationDataQuestions = (surveyDetailed) => {
        const pages = R.pathOr([], ['survey', 'pages'], surveyDetailed)
        const questions = pages.flatMap((p) => p.questions)
        this.registrationDataQuestions = questions.reduce((rq, q) => {
            if (q && q.hasPrepopulatedData) return [...rq, q.questionId]
            return rq
        }, [])
    }

    setHiddenAndMappedQuestions = (surveyDetailed) => {
        this.hiddenQuestionIds = new Set()
        this.mappedQuestionIds = new Set()
        const pages = R.pathOr([], ['survey', 'pages'], surveyDetailed)
        pages.flatMap((p) => p.questions).forEach((q) => {
            if (!q) return
            if (q.isHidden) this.hiddenQuestionIds.add(q.questionId)
            if (q.populateFrom) this.mappedQuestionIds.add(q.questionId)
        })
    }

    getEventLevelQuestions = async (metric) => {
        const { appStore, newWidget } = this
        const respondentType = R.pathOr(newWidget.respondentType, ['respondentType'], metric)
        const surveyType = R.pathOr(newWidget.surveyType, ['surveyType'], metric)
        const industry = R.pathOr(newWidget.industry, ['industry'], metric)
        const { langcode } = appStore
        const { recordId, surveys } = this.dashStore

        const surveyBasic = surveys
            .filter((s) => (
                s.productId === recordId
                && s.respondentType === respondentType
                && s.surveyType === surveyType
                && s.industry === industry
                && ((s.status !== 4
                && [1, 2, 3, 6].includes(s.productStatus)
                && s.isPrimary) || surveyType === 'EM')
            ))
            .reduce((s, cs) => (cs.year > s.year ? cs : s), { year: 0 })
        if (!surveyBasic || !surveyBasic.surveyId) {
            this.detailedQuestions = []
            return
        }
        const surveyDetailed = await this.getSurveyQuestions(surveyBasic.surveyId)

        this.fullSurvey = surveyDetailed

        this.setDataLevels()

        this.setRegistrationDataQuestions(surveyDetailed)
        this.setHiddenAndMappedQuestions(surveyDetailed)

        const surveyQuestions = surveyDetailed.survey.pages
            .reduce((q, p) => {
                q.push((p.questions || []).map((pq) => ({ ...pq, pagePriority: p.priority })))
                return q
            }, [])
            .flat(2)
            .filter((q) => !!q)

        const pageQuestionIdToQuestionId = {}
        const parentIds = {}
        const questionPriorities = {}
        surveyQuestions.forEach(({ id, questionId, parentId, pagePriority, priority: questionPriority }) => {
            pageQuestionIdToQuestionId[id] = questionId
            if (parentId) {
                parentIds[questionId] = pageQuestionIdToQuestionId[parentId]
            }
            const maxQuestionsPerPage = 1000
            questionPriorities[questionId] = (pagePriority * maxQuestionsPerPage) + questionPriority
        })

        this.questions = await Promise.all(
            surveyQuestions.map((q) => this.getQuestion(q.questionId, true)),
        )

        this.questions = this.questions
            .filter(({ question }) => !this.blockedEventLevelQuestions.includes(question.questionTypeCode))
            .map((q) => ({
                ...q,
                parentId: parentIds[q.questionId],
                priority: questionPriorities[q.questionId],
            }))

        const { metadata } = surveyDetailed.survey
        const { productInfo } = metadata
        const { industrySpecificInfo } = productInfo
        const { productType } = industrySpecificInfo
        const group = this.getRespondentTypeName(industry, productType, surveyType, respondentType)

        this.detailedQuestions = this.formatQuestions(
            this.questions,
            langcode,
            group,
            respondentType,
            true,
        )
        const key = `${industry}_${productType}_${surveyType}_${respondentType}_true`
        this.detailedQuestionsMap.set(key, this.detailedQuestions)
    }

    getSurveyQuestions = (surveyId) => this.appStore.http.get(`/survey/${surveyId}`)

    addEditingQuestionToOptions = async (metric, returnQuestion) => {
        const { appStore, dashStore } = this
        const { oId } = dashStore
        const isEventQuestion = oId === 6
        this.loadingQuestions = true
        // fetch individual question
        const url = `/api/question/${metric.questionId}`
        const question = await appStore.http.get(url)
        question.label = question.content.shortTitle[appStore.langcode]?.text || ''
        question.value = question.id
        // add it to question map
        const industry = R.pathOr(metric.industry, ['industry', 'value'], metric)
        const productType = R.pathOr(metric.productType, ['productType', 'value'], metric)
        const surveyType = R.pathOr(metric.surveyType, ['surveyType', 'value'], metric)
        const respondentType = R.pathOr(metric.respondentType, ['respondentType', 'value'], metric)
        const key = `${industry}_${productType}_${surveyType}_${respondentType}_${isEventQuestion}`
        const existing = this.detailedQuestionsMap.get(key) || []
        if (!existing.some((q) => q.value === question.id)) {
            this.detailedQuestionsMap.set(key, [...existing, question])
            this.questionCount[key] = (this.questionCount[key] || 0) + 1
        }
        this.loadingQuestions = false
        if (returnQuestion) return question
    }

    getQlistQuestions = async (metric, recordOverwrite, oIdOverwrite, respondentTypeOverwrite, surveyTypeOverwrite, productTypeOverwrite) => {
        const { appStore, newWidget } = this
        const oId = oIdOverwrite || this.dashStore.oId
        const recordId = recordOverwrite || this.dashStore.recordId
        const productType = R.pathOr(metric.productType, ['productType', 'value'], metric)
        const surveyType = R.pathOr(metric.surveyType, ['surveyType', 'value'], metric)
        const respondentType = R.pathOr(metric.respondentType, ['respondentType', 'value'], metric)
        const includeExplori = surveyType === 'EM' ? '&includeExplori=true' : ''
        const url = `/qlist/getAllQlists2?productType=${productTypeOverwrite || productType}&surveyType=${surveyTypeOverwrite || surveyType}&respondentType=${respondentTypeOverwrite || respondentType}${includeExplori}`
        const allQuestions = await appStore.http.get(url)
        const filteredQuestions = R.uniqBy(R.prop('questionId'))(
            oId > 3
                ? allQuestions.filter((q) => (
                    (q.recordId === recordId && q.qlistoid === oId) || q.qlistoid < oId))
                : allQuestions,
        )

        const uniqueQuestionIds = []
        return filteredQuestions.reduce((qs, q) => {
            const question = JSON.parse(q.question)
            if (!uniqueQuestionIds.includes(q.questionId.toLowerCase())) {
                uniqueQuestionIds.push(q.questionId.toLowerCase())
                qs.push({ ...q, question, questionOid: R.pathOr(0, ['metadata', 'oid'], question) })
            }
            return qs
        }, [])
    }

    getQuestion = async (questionId, appendQuestionId = false) => {
        const { appStore } = this
        const url = `/api/question/${questionId.toUpperCase()}/7`
        const question = await appStore.http.get(url)
        if (appendQuestionId) {
            question.questionId = questionId
            return question
        }
        return question
    }

    resetStore = () => {
        this.isEditSetupInProgress = false
        this.newWidget = new NewWidget(undefined, undefined, this.appStore)
    }

    changeNonWidgetReset = (kpi) => {
        const { newWidget } = this

        const {
            widgetBasis,
        } = newWidget
        const respondentType = R.pathOr(newWidget.respondentType, ['respondentType', 'value'], newWidget)
        const surveyType = R.pathOr(newWidget.surveyType, ['surveyType', 'value'], newWidget)
        const productType = R.pathOr(newWidget.productType, ['productType', 'value'], newWidget)
        const industry = R.pathOr(newWidget.industry, ['industry', 'value'], newWidget)

        const kpiSettings = R.pathOr({}, ['settings'], kpi)

        this.newWidget = {
            widgetBasis,
            industry,
            respondentType,
            surveyType,
            productType,
            questionId: '',
            parentQuestion: '',
            displayAs: '',
            questionTypeCode: '',
            isNumeric: '',
            question: undefined,
            dataLevel: '',
            timePeriod: '',
            filter: [],
            chart: '',
            defaultSize: 2,
            multiQuestion: [{}, {}],
            nonQuestionType: '',
            quadrant: undefined,
            widgetTitle: '',
            nonQuestionKpiType: '',
            widgetRenderSettings: {},
            kpiRenderSettings: {
                format: R.pathOr(undefined, ['format'], kpiSettings),
                dataDecimals: R.pathOr(undefined, ['decimals'], kpiSettings),
            },
            customTag: undefined,
            tagOptions: [],
            optionFilter: [],
            includeRegistrationData: false,
        }
    }

    toggleShow = (newValue) => {
        if (this.show) {
            this.resetStore()
        }
        this.show = typeof newValue === 'boolean' ? newValue : !this.show
    }

    createWidget = () => {
        // TODO: Create the dahboard with settings from wizard
        this.toggleShow()
    }

    setError = () => {
        this.error = true
    }

    setDisplayAs = ({ value }) => {
        this.newWidget.changeNewWidgetBulk([{ field: 'kpiType', value }])
    }

    getProdMap = async (getQuestionCount) => {
        const { appStore } = this
        const { clientId } = appStore.userData
        const res = await appStore.http.get(`/productmap/${clientId}?addQuestionCount=${getQuestionCount}`)
        const numOfMandatoryQuestions = res.numOfMandatoryQuestions ?? undefined
        const productMap = res.productMap ?? res

        const prodMap = productMap.reduce((obj, m) => {
            const newObj = { ...obj }
            // eslint-disable-next-line max-len
            newObj[m.industrycode] = newObj[m.industrycode] || { label: m.industryname, value: m.industrycode, children: {} }
            // eslint-disable-next-line max-len
            newObj[m.industrycode].children[m.producttypecode] = newObj[m.industrycode].children[m.producttypecode]
            || { label: m.producttypename, value: m.producttypecode, children: {} }
            // eslint-disable-next-line max-len
            newObj[m.industrycode].children[m.producttypecode].children[m.surveytypecode] = newObj[m.industrycode].children[m.producttypecode].children[m.surveytypecode]
            || { label: m.surveytypename, value: m.surveytypecode, children: {} }
            // eslint-disable-next-line max-len
            newObj[m.industrycode].children[m.producttypecode].children[m.surveytypecode].children[m.respondenttypecode] = newObj[m.industrycode].children[m.producttypecode].children[m.surveytypecode].children[m.respondenttypecode]
            || { label: m.respondenttypename, value: m.respondenttypecode }

            return newObj
        }, {})
        return { prodMap, numOfMandatoryQuestions }
    }

    addWidget = () => {
        const { dashStore } = this
        const formattedWidget = this.newWidget.newWidgetToWidget()
        dashStore.addWidget(formattedWidget)
        return formattedWidget
    }

    getNonQuestionData = async () => {
        this.loading = true
        await this.getKpis()
        this.loading = false
    }

    getKpis = async () => {
        try {
            const kpis = await this.appStore.http.get('/kpi')
            this.kpis = kpis
        } catch (e) {
            this.kpis = []
        }
    }

    getDefaultTitle = () => {
        const { newWidget, kpis, appStore } = this
        const { langcode } = appStore
        if (newWidget.widgetTitle) return newWidget.widgetTitle
        if (newWidget.nonQuestionType === 'quadrantAnalysis') return 'Quadrant Analysis'
        const respondentType = R.pathOr(newWidget.respondentType, ['respondentType', 'value'], newWidget)

        let defaultTitle
        if (newWidget.widgetBasis !== 'question') {
            defaultTitle = newWidget.nonQuestionType === 'KPI'
                ? kpis.find((k) => (
                    k.kpiId === newWidget.nonQuestionKpiType
                        && k.respondentType === respondentType
                )).name
                : newWidget.nonQuestionType
        } else {
            defaultTitle = R.pathOr('', ['question', 'content', 'shortTitle', langcode, 'text'], newWidget)
        }

        return defaultTitle
    }

    getAllowedKpis = () => {
        const allowedKpiQuestions = this.questions.reduce((set, q) => (
            set.add(q.questionId.toLowerCase())
        ), new Set())
        return this.kpis
            .filter((k) => (
                R.pathOr(
                    false,
                    [
                        'productMap',
                        k.industry,
                        'children',
                        k.productType,
                        'children',
                        k.surveyType,
                        'children',
                        k.respondentType,
                    ],
                    this,
                )
            ))
            .reduce(({ kpis, allowedKpis }, kpi) => {
                if (hasRequiredDependencies(kpi, allowedKpiQuestions, allowedKpis)) {
                    kpis.push(kpi)
                    allowedKpis.add(kpi.kpiId.toLowerCase())
                }
                return {
                    kpis,
                    allowedKpis,
                }
            }, {
                kpis: [],
                allowedKpis: new Set(),
            }).kpis
    }

    getRespondentTypeName = (industry, productType, surveyType, respondentType) => R.pathOr(
        'KPIs',
        [
            'productMap',
            industry,
            'children',
            productType,
            'children',
            surveyType,
            'children',
            respondentType,
            'label',
        ],
        this,
    )

    getGroupedDataOptions = (widget, metricIndex = 0) => {
        const allowedKpis = widget.respondentType ? this.getAllowedKpis() : []
        const kpiOptions = allowedKpis
            .filter((k) => (
                k.respondentType === R.pathOr(widget.respondentType, ['respondentType', 'value'], widget)
                        && k.industry === R.pathOr(widget.industry, ['industry', 'value'], widget)
                        && k.surveyType === R.pathOr(widget.surveyType, ['surveyType', 'value'], widget)
                        && k.productType === R.pathOr(widget.productType, ['productType', 'value'], widget)
            )).map((k) => (
                {
                    type: 'KPI',
                    label: k.name,
                    value: k.kpiId,
                    isMatrixKpi: !!k.settings.quadrantNames || k.kpiId === 'LMX',
                    matrixAxisNames: k.settings.quadrantNames
                        ? Object.fromEntries(k.variables.map(({ name }) => name.split('-'))) : undefined,
                    isMultiOptionKpi: R.pathOr([], ['formula'], k).filter((f) => f.name).length > 1,
                }
            ))

        const questionOptions = this.getMetricQuestions(widget, metricIndex)
        const groupedDataOptions = this.groupDropDownQuestions({ questionOptions, kpiOptions })
        return groupedDataOptions
    }

    getBenchmarkTags = async () => {
        const benchmarkTags = await this.appStore.http.get('/benchmarkTags')
        this.benchmarkTags = benchmarkTags.benchmarkTags
    }

    toggleShowWidgetCreator = () => {
        if (this.showWidgetCreator) this.resetStore()
        this.showWidgetCreator = !this.showWidgetCreator
    }

    getQuestionOptions = () => {
        const { newWidget, getQuestionLabel, dashStore } = this
        const { appStore } = dashStore
        const question = R.pathOr({}, ['metrics', 0, 'question'], newWidget)
        const { langcode } = appStore
        if (!question || !question.questionTypeCode) return []
        const { questionTypeCode } = question

        const questionPath = questionTypeCode.includes('TB')
            ? ['optionSetX', 'options']
            : ['optionSet', 'options']

        const options = R.pathOr([], questionPath, question)

        return options.map((o) => ({
            value: R.pathOr('', ['id'], o),
            label: unescapeHtmlTags(stripOutHtml(getQuestionLabel(
                question,
                langcode,
                R.pathOr({}, ['langs'], o),
                false,
            ))),
        }))
    }

    setOptionFilters = () => {
        const { newWidget, getQuestionOptions } = this
        if (newWidget.kpiType !== 'pct') {
            newWidget.changeNewWidgetBulk([{ value: [], field: 'optionFilter' }])
            return
        }
        newWidget.changeNewWidgetBulk([{ value: getQuestionOptions(), field: 'optionFilter' }])
    }

    toggleCollapse = (newPanel) => {
        this.setState({ activePanel: newPanel })
    }

    newMultiQuestionValue = (newValue, index) => {
        const { newWidget } = this
        const { multiQuestion } = newWidget
        const newMultiQ = [...multiQuestion]
        newMultiQ[index] = newValue
        return newMultiQ
    }

    getIndustryOptions = () => {
        const {
            productMap,
        } = this.dashStore
        return getIndustries(productMap, true)
    }

    getProductTypeOptions = (m) => {
        const { productMap, oId, productType } = this.dashStore
        const metricObject = m
        const { industry } = metricObject
        const standardValues = Object.values(R.pathOr({}, [industry, 'children'], productMap))
        if (oId === 6) {
            const productTypeObj = standardValues.find((sv) => sv.value === productType)
            return productTypeObj ? [productTypeObj] : []
        }

        if (!this.questionCount || !industry) return standardValues

        const allowedProductTypes = new Set()
        Object.keys(this.questionCount).forEach((k) => {
            const [ind, prod] = k.split('_')
            if (ind === industry) allowedProductTypes.add(prod)
        })

        return standardValues.filter((pt) => allowedProductTypes.has(pt.value))
    }

    getSurveyTypeOptions = (m) => {
        const {
            productMap,
            surveys,
            oId,
            recordId,
        } = this.dashStore
        const metricObject = m
        const { industry, productType } = metricObject

        const surveyTypesInEvent = oId === 6
            ? [...new Set(surveys
                .filter((s) => s.productId === recordId)
                .map((s) => s.surveyType))]
            : undefined

        const surveyTypeOptions = Object.values(
            R.pathOr({}, [industry, 'children', productType, 'children'], productMap),
        ).filter((st) => st.value !== 'GE')
            .filter((st) => !surveyTypesInEvent || surveyTypesInEvent.includes(st.value))
        // "|| oId === 6" because event level should show options regardless of mandatory questions
        if (!this.questionCount || !industry || !productType || oId === 6) return surveyTypeOptions

        const allowedSurveyTypes = new Set()
        Object.keys(this.questionCount).forEach((key) => {
            const [ind, prod, surv] = key.split('_')
            if (ind === industry && prod === productType) allowedSurveyTypes.add(surv)
        })

        return surveyTypeOptions.filter((st) => allowedSurveyTypes.has(st.value))
    }

    getRespondentTypeOptions = (m) => {
        const {
            productMap,
            oId,
        } = this.dashStore
        const metricObject = m
        const { industry, productType, surveyType } = metricObject

        const respondentTypeOptions = Object.values(R.pathOr(
            {},
            [industry, 'children', productType, 'children', surveyType, 'children'],
            productMap,
        ))
        // "|| oId === 6" because event level should show options regardless of mandatory questions
        if (!this.questionCount || !industry || !productType || !surveyType || oId === 6) return respondentTypeOptions

        const allowedRespondentTypes = new Set()
        Object.keys(this.questionCount).forEach((k) => {
            const [ind, prod, surv, resp] = k.split('_')
            if (ind === industry && prod === productType && surv === surveyType) {
                allowedRespondentTypes.add(resp)
            }
        })

        return respondentTypeOptions.filter((rt) => allowedRespondentTypes.has(rt.value))
    }

    getDisplayAsOptions = () => {
        const { newWidget } = this
        const { isNumeric, metrics } = newWidget
        const questionTypeCode = R.pathOr('', ['metrics', 0, 'question', 'questionTypeCode'], newWidget)
        const options = R.pathOr(
            [],
            ['questionDisplayAs', questionTypeCode, isNumeric],
            this,
        )
        // option "pct" shows answer options as series, we don't want it in multimetric
        if (metrics.length > 1) return options.filter((o) => o.value !== 'pct')
        return options
    }

    setDefaultDisplayAs = () => {
        const options = this.getDisplayAsOptions()
        if (options.length < 1) return
        const defaultOrder = ['avg', 'nps', 'pct']
        const selected = defaultOrder.find((d) => options.some((o) => o.value === d))
        if (selected) this.newWidget.kpiType = selected
    }

    blurDisplayAs = (v) => {
        this.setDisplayAs(v)
        this.setOptionFilters()
    }

    changeDataLevel = (value, field) => {
        const { changeNewWidgetBulk } = this.newWidget
        this.createTreeData(
            value,
            R.pathOr([], ['newWidget', 'filter'], this),
        )
        changeNewWidgetBulk([{ value: value.value, field }])
    }

    getTimePeriodOptions = () => {
        const { dashStore, newWidget } = this
        const { oId } = dashStore

        return oId === 6 ? [
            { value: 'YOY', label: 'Year On Year' },
        ]
            : R.pathOr([], ['timePeriodOptions', newWidget.scope], this)
    }

    closeShowWidgetCreator = () => {
        const { dashStore, resetStore } = this
        const { toggleShowWidgetCreator } = dashStore
        resetStore()
        toggleShowWidgetCreator()
    }

    updateLayout = (newKey) => {
        const { widgetKey } = this.props
        const { dashStore } = this
        const { layouts } = dashStore
        return Object.keys(layouts).reduce((layout, k) => (
            {
                ...layout,
                [k]: layouts[k]
                    .map((l) => ({ ...l, i: l.i === widgetKey ? newKey : l.i })),
            }
        ), {})
    }

    updateWidget = () => {
        const { widgetKey } = this.props
        const { dashStore } = this
        const { updateWidget, updateLayouts, saveDashboard } = dashStore
        const widgets = R.pathOr([{}], ['widgets'], dashStore)
        const widgetIndex = widgets.findIndex((w) => w.key === widgetKey)
        const newKey = uuidv4()
        const newLayout = this.updateLayout(newKey)
        const widget = this.addWidget(true)
        widget.key = newKey
        updateWidget(widgetIndex, widget)
        updateLayouts(newLayout)
        saveDashboard()
    }

    toggleShowWidgetCreatorV3 = () => {
        this.showWidgetCreatorV3 = !this.showWidgetCreatorV3
    }

    groupDropDownQuestions = ({ questionOptions, kpiOptions }) => {
        const [exploriGroup, companyGroup, surveyGroup] = [
            i18n.t('dynamicDash.data.group.explori'),
            i18n.t('dynamicDash.data.group.company'),
            i18n.t('dynamicDash.data.group.survey'),
        ]
        const groupedQuestions = questionOptions.reduce((res, question) => {
            const { oid } = question.metadata
            let groupName
            if (oid === 1) groupName = exploriGroup
            if (oid >= 3 && oid <= 5) groupName = companyGroup
            if (oid >= 6) groupName = surveyGroup
            const groupPos = res.findIndex((g) => g.label === groupName)

            if (groupPos >= 0) res[groupPos].options.push(question)
            else res.push({ label: groupName, options: [question] })

            return res
        }, []).sort((a, b) => {
            const groupNames = [exploriGroup, companyGroup, surveyGroup]
            return groupNames.indexOf(a.label) - groupNames.indexOf(b.label)
        })
        groupedQuestions.forEach((g) => {
            // lower values = higher priority
            g.options.sort((a, b) => (a.priority) - (b.priority))
        })
        const groupedKpis = [{ label: i18n.t('dynamicDash.data.group.otherMetrics'), options: kpiOptions }]
        return [
            { label: i18n.t('form.general.please_select_ellipsis'), options: [] },
            ...groupedQuestions,
            ...groupedKpis]
    }

    shouldGetQuestions = (industry, productType, surveyType, respondentType) => {
        const { oId, recordId, surveys } = this.dashStore
        const isEventLevel = oId === 6
        if (isEventLevel) {
            const hasSurvey = surveys
                .filter((s) => (
                    s.productId === recordId
                    && s.respondentType === respondentType
                    && s.surveyType === surveyType
                    && s.industry === industry
                    && ((s.status !== 4
                    && [1, 2, 3, 6].includes(s.productStatus)
                    && s.isPrimary) || surveyType === 'EM')
                )).length
            if (!hasSurvey) return false
        }

        const allFieldsSet = [industry, productType, surveyType, respondentType].every(Boolean)
        const alreadyFetched = this.detailedQuestionsMap.has(
            `${industry}_${productType}_${surveyType}_${respondentType}_${isEventLevel}`,
        )
        return allFieldsSet && !alreadyFetched
    }

    autoSetMetricField = (widgetMetric, field, options) => {
        let value
        let extraFields = []
        if (options.length === 1) {
            value = options[0].value
            if (field === 'respondentType') {
                extraFields = [{ value: options[0].value, field }, { value: options[0].label, field: 'group' }]
            }
        } else if (widgetMetric[field] && options.some((o) => o.value === widgetMetric[field])) {
            value = widgetMetric[field] // keep previous selections if valid
        }

        widgetMetric.changeNewWidgetMetricBulk([{ value, field }, ...extraFields])
    }

    renderWidgetMetricControls = (widgetMetric, metricIndex) => {
        const industryOptions = this.getIndustryOptions()
        const productTypes = this.getProductTypeOptions(widgetMetric)
        const surveyTypes = this.getSurveyTypeOptions(widgetMetric)
        const respondentTypes = this.getRespondentTypeOptions(widgetMetric)
        this.autoSetMetricField(widgetMetric, 'industry', industryOptions)
        this.autoSetMetricField(widgetMetric, 'productType', productTypes)
        this.autoSetMetricField(widgetMetric, 'surveyType', surveyTypes)
        this.autoSetMetricField(widgetMetric, 'respondentType', respondentTypes)

        this.getQuestions(widgetMetric)
        const groupedDataOptions = this.getGroupedDataOptions(widgetMetric, metricIndex)

        const controls = [
            {
                name: 'industry',
                label: i18n.t('industry'),
                type: 'select',
                options: () => industryOptions,
                onChange: async (v) => {
                    widgetMetric.changeNewWidgetMetricBulk([
                        { value: undefined, field: 'question' },
                        { value: undefined, field: 'questionTypeCode' },
                        { value: v.value, field: 'industry' }])
                },
                isMandatory: true,
                dontAutoSet: false,
                isMulti: false,
                show: (store) => {
                    if (metricIndex > 0) return 0
                    if (industryOptions.length === 1 && widgetMetric.industry) return 0
                    if (industryOptions < 2) return 2
                    return 2
                },
            },
            {
                name: 'productType',
                label: i18n.t('dynamicDash.eventType'),
                type: 'select',
                options: () => productTypes,
                onChange: async (v) => {
                    widgetMetric.changeNewWidgetMetricBulk([
                        { value: undefined, field: 'question' },
                        { value: undefined, field: 'questionTypeCode' },
                        { value: v.value, field: 'productType' }])
                },
                isMandatory: true,
                dontAutoSet: false,
                isMulti: false,
                show: (store) => {
                    if (metricIndex > 0) return 0
                    if (productTypes.length === 1 && widgetMetric.productType) return 0
                    if (productTypes < 2) return 2
                    return 2
                },
            },
            {
                name: 'surveyType',
                label: i18n.t('dynamicDash.surveyType'),
                type: 'select',
                options: () => surveyTypes,
                onChange: async (v) => {
                    widgetMetric.changeNewWidgetMetricBulk([
                        { value: undefined, field: 'question' },
                        { value: undefined, field: 'questionTypeCode' },
                        { value: v.value, field: 'surveyType' }])
                },
                isMandatory: true,
                dontAutoSet: false,
                isMulti: false,
                show: (store) => {
                    if (metricIndex > 0) return 0
                    if (surveyTypes.length === 1 && widgetMetric.surveyType) return 0
                    if (surveyTypes < 2) return 2
                    return 2
                },
            },
            {
                name: 'respondentType',
                label: i18n.t('dynamicDash.respondentType'),
                type: 'select',
                options: () => respondentTypes,
                onChange: async (v) => {
                    widgetMetric.changeNewWidgetMetricBulk([
                        { value: undefined, field: 'question' },
                        { value: undefined, field: 'questionTypeCode' },
                        { value: v.value, field: 'respondentType' },
                        { value: v.label, field: 'group' },
                    ])
                    this.newWidget.changeNewWidgetBulk([{ value: '', field: 'kpiType' }])
                },
                isMandatory: true,
                dontAutoSet: false,
                isMulti: false,
                show: (store) => {
                    if (respondentTypes.length === 1 && widgetMetric.respondentType) return 0
                    if (respondentTypes < 2) return 2
                    return 2
                },
            },
            {
                name: 'question',
                label: i18n.t('dynamicDash.data'),
                type: 'select',
                options: () => groupedDataOptions,
                onChange: (v) => {
                    widgetMetric.changeNewWidgetMetricBulk([
                        { value: v, field: 'question' },
                        { value: v.label, field: 'metricLabel' },
                        { value: v.questionTypeCode, field: 'questionTypeCode' },
                    ])
                    this.newWidget.changeNewWidgetBulk(
                        [
                            {
                                value: this.dataLevels
                                    .find((dl) => dl.value === 'product')?.value || this.dataLevels[0]?.value,
                                field: 'scope',
                            },
                            { value: v.label, field: 'title' },
                        ],
                    )
                    const timePeriodOptions = this.getTimePeriodOptions()
                    this.newWidget.changeNewWidgetBulk([
                        {
                            value: timePeriodOptions
                                .find((dl) => dl.value === 'YOY')?.value || timePeriodOptions[0].value,
                            field: 'benchmarkType',
                        },
                    ])
                    const hasOnlyOneMetric = this.newWidget.metrics.length === 1
                    if (hasOnlyOneMetric) {
                        this.newWidget.setIsNumeric(v, this.dashStore.oId)
                        if (v.type !== 'KPI') {
                            this.blurDisplayAs(this.getDisplayAsOptions()[0])
                        }
                    }
                    const chartTypeOptions = this.getAllowedCharts()
                    this.newWidget.changeNewWidgetBulk([
                        {
                            value: chartTypeOptions
                                .find((dl) => dl.value === 'column')?.value || chartTypeOptions[0].value,
                            field: 'chartType',
                        },
                    ])
                    this.setDefaultDisplayAs()
                    this.setDefaultWidgetRenderSettings()
                },
                isMandatory: true,
                dontAutoSet: false,
                isMulti: false,
                show: (store) => {
                    if (!widgetMetric.respondentType || this.loadingQuestions) return 1
                    return 2
                },
                menuPortalTarget: document.body,
                styles: {
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                },
                loading: this.loadingQuestions,
                indentation: true,
            },
        ]
        return (
            <div
                className="controlBlock"
                key={widgetMetric.id}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography type="h4" style={{ marginBottom: '16px' }}>
                        {i18n.t('dynamicDash.wizard.controlBlock.title')}
                        {' - '}
                        {metricIndex + 1}
                    </Typography>
                    {metricIndex > 0
                        ? (
                            <div
                                className="removeMetric"
                                onClick={() => this.newWidget.removeMetric(metricIndex)}
                            >
                                Remove
                                <i className="fa-light fa-trash-can" />
                            </div>
                        )
                        : null}
                </div>
                <StagedForm
                    store={this}
                    controls={controls}
                    valueHolder={widgetMetric}
                />
            </div>
        )
    }

    getShowDataLevelControls = () => {
        const { newWidget } = this
        const isSliderOn = this.showAdvReportingSettings
        const showAdvField = this.getShowAdvDataLevelControls()
        return {
            scope: () => !(R.pathOr([], ['dataLevels'], this).length < 2 && newWidget.scope),
            benchmarkType: () => !(this.getTimePeriodOptions().length < 2 && newWidget.benchmarkType),
            kpiType: () => this.newWidget.metrics[0].question
                && !(this.getDisplayAsOptions().length < 2 && newWidget.chartType),
            optionFilter: () => showAdvField.optionFilter() && isSliderOn,
            filter: () => showAdvField.filter() && isSliderOn,
            customTag: () => showAdvField.customTag() && isSliderOn,
            tagOptions: () => showAdvField.tagOptions() && isSliderOn,
            branchFilter: () => showAdvField.branchFilter() && isSliderOn,
        }
    }

    getShowAdvDataLevelControls = () => {
        const { newWidget, dashStore, benchmarkTags } = this
        return {
            optionFilter: () => newWidget.kpiType === 'pct',
            filter: () => newWidget.scope !== 'client'
                && dashStore.oId !== 6
                && newWidget.scope,
            customTag: () => process.env.USE_CUSTOM_BENCHMARKS === 'true'
                && benchmarkTags.length
                && dashStore.oId !== 6
                && newWidget.scope,
            tagOptions: () => process.env.USE_CUSTOM_BENCHMARKS === 'true'
                && newWidget.customTag
                && benchmarkTags.length
                && dashStore.oId !== 6
                && newWidget.scope,
            branchFilter: () => newWidget.scope === 'branch' && this.showAdvReportingSettings,
        }
    }

    shouldShowDataLevelControls = () => Object.values(this.getShowDataLevelControls()).some((fn) => fn && fn())

    shouldShowAdvancedSlider = () => Object.values(this.getShowAdvDataLevelControls()).some((fn) => fn && fn())

    getDataLevelControls = () => {
        const showFns = this.getShowDataLevelControls()
        const controls = [
            {
                name: 'scope',
                label: i18n.t('dynamicDash.wizard.dataLevelLabel'),
                type: 'select',
                options: () => R.pathOr([], ['dataLevels'], this),
                onChange: async (v) => {
                    this.changeDataLevel(v, 'scope')
                },
                isMandatory: true,
                dontAutoSet: false,
                isMulti: false,
                show: (store) => (showFns.scope() ? 2 : 0),
                menuPortalTarget: document.body,
                styles: {
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                },
            },
            {
                name: 'benchmarkType',
                label: i18n.t('dynamicDash.wizard.timePeriodLabel'),
                type: 'select',
                options: () => this.getTimePeriodOptions(),
                onChange: async (v) => {
                    this.newWidget.changeNewWidgetBulk([{ value: v.value, field: 'benchmarkType' }])
                },
                isMandatory: true,
                dontAutoSet: false,
                isMulti: false,
                show: (store) => (showFns.benchmarkType() ? 2 : 0),
                menuPortalTarget: document.body,
                styles: {
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                },
            },
            {
                name: 'kpiType',
                label: i18n.t('dynamicDash.wizard.displayAsLabel'),
                type: 'select',
                options: () => this.getDisplayAsOptions(),
                onChange: (v) => {
                    this.blurDisplayAs(v)
                    const newChartType = this.getDefaultChartType()
                    this.newWidget.changeNewWidgetBulk([{ value: newChartType, field: 'chartType' }])
                    this.setDefaultWidgetRenderSettings()
                },
                isMandatory: true,
                dontAutoSet: false,
                isMulti: false,
                show: (store) => (showFns.kpiType() ? 2 : 0),
                menuPortalTarget: document.body,
                styles: {
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                },
            },
            {
                name: 'optionFilter',
                type: 'select',
                label: i18n.t('dynamicDash.wizard.optionFiltersLabel'),
                tooltipText: i18n.t('dynamicDash.wizard.optionFiltersTooltip'),
                options: () => this.getQuestionOptions(),
                onChange: (v) => {
                    this.newWidget.changeNewWidgetBulk([{ value: v.value, field: 'optionFilter' }])
                },
                isMandatory: false,
                dontAutoSet: true,
                isMulti: true,
                show: (store) => (showFns.optionFilter() ? 2 : 0),
                showNumberSelected: true,
                menuPortalTarget: document.body,
                styles: {
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                },
            },
            {
                name: 'branchFilter',
                type: 'select',
                label: i18n.t('dynamicDash.wizard.branchFiltersLabel'),
                tooltipText: i18n.t('dynamicDash.wizard.branchFiltersTooltip'),
                options: () => this.dashStore.surveys.reduce((so, s) => {
                    if (s.parentSurveyId) {
                        so.push({ label: s.surveyName, value: s.surveyId })
                    }
                    return so
                }, []),
                onChange: (v) => {
                    this.newWidget.changeNewWidgetBulk([{ value: v.value, field: 'branchFilter' }])
                },
                isMandatory: false,
                dontAutoSet: true,
                isMulti: true,
                show: () => (showFns.branchFilter() ? 2 : 0),
                showNumberSelected: true,
                menuPortalTarget: document.body,
                styles: {
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                },
            },
            {
                name: 'filter',
                type: 'treeViewSelect',
                label: i18n.t('dynamicDash.wizard.optionalFiltersLabel'),
                tooltipText: i18n.t('dynamicDash.wizard.optionalFiltersTooltip'),
                options: (store) => {
                    store.createTreeData(
                        R.pathOr([], ['newWidget', 'scope'], store),
                        R.pathOr([], ['newWidget', 'filter'], store),
                    )

                    return store.filterTree
                },
                onChange: (v) => { this.newWidget.changeNewWidgetBulk([{ value: v.value, field: 'filter' }]) },
                show: (store) => (showFns.filter() ? 2 : 0),
                validate: (({ value }) => !!value.length),
            },
            process.env.USE_CUSTOM_BENCHMARKS === 'true' ? {
                name: 'customTag',
                type: 'select',
                label: i18n.t('dynamicDash.wizard.groupByLabel'),
                tooltipText: i18n.t('dynamicDash.wizard.groupByTooltip'),
                options: (store) => [
                    { label: 'None', value: 0, tagOptions: [] },
                    ...store.benchmarkTags.map((b) => (
                        {
                            value: b.customTagId,
                            label: b.parentTagName || b.customTagName,
                            tagOptions: b.tagOptions,
                        }
                    )),
                ],
                onChange: (v) => {
                    this.newWidget.changeNewWidgetBulk([{ value: v.value || undefined, field: 'customTag' }])
                    this.newWidget.changeNewWidgetBulk([{
                        value: v.tagOptions.map((b) => {
                            const tagOptionConfig = JSON.parse(b.tagOptionConfig)
                            const label = tagOptionConfig.name || tagOptionConfig.langs.en_GB
                            return (
                                { value: b.tagOptionId, label }
                            )
                        }),
                        field: 'tagOptions',
                    }])
                    this.setDefaultWidgetRenderSettings()
                },
                dontAutoSet: true,
                isMandatory: false,
                show: (store) => (showFns.customTag() ? 2 : 0),
                menuPortalTarget: document.body,
                styles: {
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                },
            } : undefined,
            process.env.USE_CUSTOM_BENCHMARKS === 'true' ? {
                name: 'tagOptions',
                type: 'select',
                label: i18n.t('dynamicDash.wizard.customFiltersLabel'),
                tooltipText: i18n.t('dynamicDash.wizard.customFiltersTooltip'),
                options: (store) => {
                    const { langcode } = store.appStore
                    const customTag = store.benchmarkTags
                        .find((b) => b.customTagId === R.pathOr(undefined, ['customTag'], store.newWidget))
                    return R.pathOr([], ['tagOptions'], customTag)
                        .map((b) => {
                            const parsedTagConfig = JSON.parse(b.tagOptionConfig)
                            const tagName = R.pathOr(
                                R.pathOr(
                                    R.pathOr('', ['langs', 'en_GB'], parsedTagConfig),
                                    ['langs', langcode],
                                    parsedTagConfig,
                                ),
                                ['name'], parsedTagConfig,
                            )
                            return { value: b.tagOptionId, label: tagName }
                        })
                },
                isMulti: true,
                onChange: (v) => {
                    this.newWidget.changeNewWidgetBulk([{ value: v.value, field: 'tagOptions' }])
                },
                dontAutoSet: true,
                isMandatory: false,
                show: (store) => (showFns.tagOptions() ? 2 : 0),
                menuPortalTarget: document.body,
                styles: {
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                },
                showNumberSelected: true,
            } : undefined,
        ]
        return controls
    }

    getDefaultChartType = () => {
        const { newWidget } = this
        const { metrics, kpiType } = newWidget
        const allowedCharts = this.getAllowedCharts()
        let newChartType = allowedCharts[0].value

        if (metrics?.length < 2 && kpiType === 'pct') {
            const hasStackedColChart = allowedCharts.find((v) => v.value === 'stackedColumn')
            const hasColChart = allowedCharts.find((v) => v.value === 'column')
            if (metrics[0].questionTypeCode === 'TB' && hasStackedColChart) {
                newChartType = 'stackedColumn'
            } else if (hasColChart) {
                newChartType = 'column'
            }
        }
        return newChartType
    }

    renderDataLevelControls = () => {
        const controls = this.getDataLevelControls()
        return (
            <>
                {this.shouldShowAdvancedSlider()
                    ? (
                        <div style={{ margin: '8px', width: '115px' }} key={this.advReportingSettings.name}>
                            <Slider
                                label={this.advReportingSettings.label}
                                name={this.advReportingSettings.name}
                                onChange={() => { this.showAdvReportingSettings = !this.showAdvReportingSettings }}
                                defaultChecked={this.showAdvReportingSettings}
                            />
                        </div>
                    )
                    : null}
                <div className="controls">
                    <div className="controlBlock">
                        <Typography type="h4" style={{ marginBottom: '16px' }}>
                            {i18n.t('dynamicDash.wizard.controlBlock.title.2')}
                        </Typography>
                        <StagedForm
                            store={this}
                            controls={controls}
                            valueHolder={this.newWidget}
                        />
                    </div>
                </div>
            </>
        )
    }

    renderWidgetDisplayOptions = () => {
        const controls = [
            {
                name: 'chartType',
                label: 'Chart type',
                type: 'select',
                options: () => this.getAllowedCharts(),
                onChange: async (v) => {
                    this.newWidget.changeNewWidgetBulk([{ value: v.value, field: 'chartType' }])
                    this.setDefaultWidgetRenderSettings()
                },
                isMandatory: true,
                dontAutoSet: false,
                isMulti: false,
                show: (store) => 2,
                menuPortalTarget: document.body,
                styles: {
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                },
            },
        ]
        return (
            <div style={{ marginTop: '16px' }} className="displayOptionsControlBlock chartType">
                <StagedForm
                    store={this}
                    controls={controls}
                    valueHolder={this.newWidget}
                />
            </div>
        )
    }

    getAllowedCharts = () => {
        const { newWidget, dashStore } = this
        const { getChartScope, oId } = dashStore
        const { kpiType, isNumeric } = newWidget
        const dataLevel = (newWidget.scope === 'product' ? 'event' : newWidget.scope) === getChartScope(oId) ? 'currentLevel' : 'levelBelow'
        let allowedCharts = []

        if (newWidget.metrics.length === 1) {
            const isKpi = R.pathOr('', [0, 'question', 'type'], newWidget.metrics) === 'KPI'
            const isMatrix = R.pathOr(false, [0, 'question', 'isMatrixKpi'], newWidget.metrics)
            const isHeat = R.pathOr('', [0, 'question', 'value'], newWidget.metrics) === 'EEM'
            const isVMAT = R.pathOr('', [0, 'question', 'value'], newWidget.metrics) === 'CIMV'

            let kpiKey = isMatrix ? 'LMX' : 'KPI'

            if (isHeat && oId === 6) kpiKey = 'heat' // heat map is only for product level dashboard
            if (isVMAT && oId === 6) kpiKey = 'VMAT' // heat map is only for product level dashboard

            const chartPath = isKpi
                ? [
                    kpiKey,
                    dataLevel,
                ]
                : [
                    R.pathOr('', [0, 'question', 'questionTypeCode'], newWidget.metrics),
                    newWidget.isNumeric,
                    dataLevel,
                ]

            const resolvedCharts = R.pathOr(
                R.pathOr([], chartPath, this.questionChartOptions),
                [...chartPath, newWidget.kpiType],
                this.questionChartOptions,
            )
            allowedCharts = [...(Array.isArray(resolvedCharts) ? resolvedCharts : [])]
            if (kpiType === 'nps' && isNumeric === 'nps') {
                allowedCharts = allowedCharts.filter((c) => !['stackedColumn', 'stackedBar'].includes(c))
            }
        } else if (newWidget.metrics.length > 1) {
            const chartPath = ['multiQuestion', dataLevel]
            allowedCharts = [...R.pathOr([], chartPath, this.questionChartOptions)]
                .filter((c) => !['stackedColumn', 'stackedBar'].includes(c))
            if ([2, 3].includes(newWidget.metrics.length)) {
                allowedCharts.push('quadrantAnalysis')
            }
        } else if (!['KPI', 'multiQuestion'].includes(newWidget.nonQuestionType) && newWidget.kpiType === 'nps') {
            allowedCharts = [...R.pathOr(
                [],
                [newWidget.questionTypeCode, newWidget.kpiType, dataLevel],
                this.questionChartOptions,
            )]
        } else {
            const chartPath = ['KPI', 'multiQuestion'].includes(newWidget.nonQuestionType)
                ? [newWidget.nonQuestionKpiType === 'LMX' || newWidget.isMatrixKpi ? 'LMX' : newWidget.nonQuestionType, dataLevel]
                : [newWidget.questionTypeCode, newWidget.isNumeric, dataLevel]

            allowedCharts = ['KPI', 'multiQuestion'].includes(newWidget.nonQuestionType)
                ? [...R.pathOr([], chartPath, this.questionChartOptions)]
                : [...R.pathOr(
                    R.pathOr([], chartPath, this.questionChartOptions),
                    [...chartPath, newWidget.kpiType],
                    this.questionChartOptions,
                )]
        }

        return Array.isArray(allowedCharts) ? allowedCharts.map((v) => (this.chartTypes[v])) : []
    }

    getDefaultSortBy = (newWidget, dashStore) => {
        if (newWidget.customTag !== undefined) return 'tagOptionName'
        if (newWidget.scope === 'product' && dashStore.oId !== 6) return 'endDate'
        return 'none'
    }

    setDefaultWidgetRenderSettings = () => {
        const { dashStore, newWidget } = this
        const {
            chartType, isCurrency, kpiType,
        } = newWidget
        const pctFormat = kpiType === 'pct' ? '%' : 'numeric'

        const widgetRenderSettings = {
            showLegend: true,
            showDataLabels: true,
            labelPosition: this.defaultLabelPositions[chartType] ?? this.defaultLabelPositions.default,
            showOptionLabels: false,
            rotateXAxisLabels: !['bar', 'stackedBar'].includes(chartType),
            orientation: 'horizontal',
            swapSeriesXAxis: false,
            format: isCurrency ? 'currency' : pctFormat,
            sortBy: this.getDefaultSortBy(newWidget, dashStore),
            sortDirection: 1,
            removeZeroNull: 'dontRemove',
            dataDecimals: ['text', 'gauge'].includes(chartType) || kpiType === 'avg' ? 2 : 0,
            showTitle: false,
            showYAxis: true,
            scrollable: false,
        }

        newWidget.changeNewWidgetBulk([{
            value: widgetRenderSettings,
            field: 'widgetRenderSettings',
        }])
    }

    isQuestionValidAsSecondaryMetric = (metric, question) => {
        const { questionTypeCode } = question
        const questionIsNumericField = this.newWidget.getIsNumeric(question, this.dashStore.oId)
        const behavesAsNumeric = ['numeric', 'nps'].includes(questionIsNumericField)
        const isKpi = R.pathOr(false, ['question', 'type'], metric) === 'KPI'

        return (isKpi && !question.isMatrixKpi && !question.isMultiOptionKpi) || (
            ['RD', 'SB', 'TB - row'].includes(questionTypeCode)
            && behavesAsNumeric)
    }

    getMetricQuestions = (metric, metricIndex = 0) => {
        const { oId } = this.dashStore
        const productType = R.pathOr(metric.productType, ['productType', 'value'], metric)
        const surveyType = R.pathOr(metric.surveyType, ['surveyType', 'value'], metric)
        const industry = R.pathOr(metric.industry, ['industry', 'value'], metric)
        const respondentType = R.pathOr(metric.respondentType, ['respondentType', 'value'], metric)
        const isEventQuestion = oId === 6
        const options = this.detailedQuestionsMap.get(`${industry}_${productType}_${surveyType}_${respondentType}_${isEventQuestion}`) || []
        if (metricIndex > 0) {
            return options.filter((question) => this.isQuestionValidAsSecondaryMetric(metric, question))
        }
        return options
    }

    toggleV3WidgetEdit = async (chartKey) => {
        const { widgets, appStore } = this.dashStore
        // setup editing variables
        const editingWidget = widgets.find(({ key }) => key === chartKey)
        editingWidget.editing = true
        this.isEditSetupInProgress = true
        editingWidget.previousKey = chartKey
        this.toggleShowWidgetCreatorV3()

        // build Widget & get question info
        this.newWidget = new NewWidget(editingWidget, this.dashStore, appStore)
        await Promise.all(this.newWidget.metrics.map((m) => this.getQuestions(m)))

        // setup Widget metrics
        const kpiOptions = this.getAllowedKpis()
        await Promise.all(this.newWidget.metrics.map(async (metric, metricIndex) => {
            const questions = [
                ...this.getMetricQuestions(metric, metricIndex),
                ...kpiOptions.filter((k) => (
                    k.respondentType === R.pathOr(metric.respondentType, ['respondentType', 'value'], metric)
                        && k.industry === R.pathOr(metric.industry, ['industry', 'value'], metric)
                        && k.surveyType === R.pathOr(metric.surveyType, ['surveyType', 'value'], metric)
                        && k.productType === R.pathOr(metric.productType, ['productType', 'value'], metric)
                )).map((k) => (
                    {
                        type: 'KPI',
                        label: k.name,
                        value: k.kpiId,
                        isMatrixKpi: !!k.settings.quadrantNames || k.kpiId === 'LMX',
                        matrixAxisNames: k.settings.quadrantNames ? Object.fromEntries(k.variables.map(({ name }) => name.split('-'))) : undefined,
                    }
                )),
            ]

            // set Widget metrics Questions
            const isKpi = metric.questionId === '00000000-0000-0000-0000-000000000000'
            const kpiTypeOverwrite = editingWidget.kpiType === 'LMAT' ? 'LMX' : editingWidget.kpiType // LMAT is LMX now
            let question = isKpi
                ? questions.find((q) => q.value === kpiTypeOverwrite)
                : questions.find((q) => q.value === metric.questionId)

            if (!question) { // question is probably not in QList anymore
                question = await this.addEditingQuestionToOptions(metric, true)
            }
            metric.changeNewWidgetMetricBulk([
                { value: question, field: 'question' },
                { value: question.label, field: 'metricLabel' },
                { value: question.questionTypeCode, field: 'questionTypeCode' },
            ])
        }))

        // setup 'Reporting settings'
        const firstQuestion = R.pathOr(false, ['metrics', 0, 'question'], this.newWidget)
        if (firstQuestion) this.newWidget.setIsNumeric(firstQuestion, this.dashStore.oId)
        const displayAsOptions = this.getDisplayAsOptions()
        if (!displayAsOptions.some((o) => o.value === this.newWidget.kpiType)) this.setDefaultDisplayAs()

        // end
        this.isEditSetupInProgress = false
    }
}

export default WidgetCreatorStore
